import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Eye, 
  Mail,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  FileText,
  Trash2,
  UserPlus,
  UserCog,
  FileSignature,
  PenTool,
  RefreshCw
} from 'lucide-react';

/**
 * Componenta pentru tabelul de utilizatori
 */
const UsersTable = ({ 
  users, 
  selectedUsers, 
  toggleUserSelection,
  toggleSelectAllUsers,
  handleSendEmail,
  handleDeleteUser,
  page,
  setPage,
  totalPages,
  total,
  limit,
  fetchUsers
}) => {
  const { currentUser } = useAuth();
  const [isUpdatingSubmission, setIsUpdatingSubmission] = useState(null);
  const [isUpdatingIneligible, setIsUpdatingIneligible] = useState(null);
  const [generatingAuthority, setGeneratingAuthority] = useState(null);
  const navigate = useNavigate();

  // Role colors
  const roleColors = {
    'admin': 'bg-purple-100 text-purple-700',
    'partner': 'bg-blue-100 text-blue-700',
    'client': 'bg-green-100 text-green-700',
  };

  // Generam inițialele pentru utilizator
  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ')
      .filter(part => part.length > 0)
      .map(part => part[0].toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Formatare dată pentru afișare
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('ro-RO', options);
  };

  // Get creator name
  const getCreatorName = (user) => {
    if (!user.added_by) return 'N/A';
    return typeof user.added_by === 'object' ? user.added_by.name : 'Necunoscut';
  };

  // Handle authority document generation
  const handleGenerateAuthority = async (user, e) => {
    e.stopPropagation();
    
    // Check if user has signature
    if (!user.signature) {
      const confirmMessage = `Utilizatorul ${user.name} nu are semnătură salvată. Împuternicirea va fi generată fără semnătură. Doriți să continuați?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }
    
    try {
      setGeneratingAuthority(user._id);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
      
      const response = await axios.post(
        `${API_URL}/admin/contracts/admin/generate-authority/${user._id}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data && response.data.success) {
        const successMessage = user.signature 
          ? 'Împuternicirea a fost generată cu succes cu semnătura utilizatorului!' 
          : 'Împuternicirea a fost generată cu succes (fără semnătură)!';
        alert(successMessage);
        fetchUsers(); // Refresh the user list
      }
    } catch (error) {
      console.error('Error generating authority document:', error);
      alert(`Eroare la generarea împutternicirii: ${error.response?.data?.message || error.message}`);
    } finally {
      setGeneratingAuthority(null);
    }
  };

  // Handle authority document regeneration
  const handleRegenerateAuthority = async (user, e) => {
    e.stopPropagation();
    
    const confirmMessage = `Doriți să regenerați împuternicirea pentru utilizatorul ${user.name}? Documentul existent va fi suprascris.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Reuse the same generation function
    await handleGenerateAuthority(user, e);
  };

  // Handle authority document download
  const handleDownloadAuthority = async (user, e) => {
    e.stopPropagation();
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
      
      const response = await axios.get(
        `${API_URL}/admin/users/${user._id}/download-authority-document`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob'
        }
      );

      // Verificăm dacă răspunsul este un blob valid
      if (response.data && response.data.size > 0) {
        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        const fileName = `imputernicire_${user.name?.replace(/\s+/g, '_') || user._id}.pdf`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Înțelege-se că răspunsul este gol sau invalid');
      }
    } catch (error) {
      console.error('Error downloading authority document:', error);
      
      // Verificăm tipul de eroare pentru a oferi mesaje mai clare
      if (error.response?.status === 400) {
        alert('Eroare de cerere: Verificați dacă utilizatorul există și are împuternicirea generată.');
      } else if (error.response?.status === 404) {
        if (window.confirm('Împuternicirea nu există. Doriți să o generați acum?')) {
          handleGenerateAuthority(user, e);
        }
      } else if (error.response?.status === 500) {
        alert('Eroare de server: Încercați din nou într-un moment.');
      } else {
        alert(`Eroare la descărcarea împuternicirii: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  return (
    <>
      {/* Tabel Utilizatori */}
      <div className="overflow-x-auto">
        <div className="glassmorphism p-1 rounded-xl shadow-lg mb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-white/70">
                <th scope="col" className="p-4 text-left">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={toggleSelectAllUsers}
                    />
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nume
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CNP
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documente
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Depus
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Neeligibil
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Adăugat de
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data înregistrării
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acțiuni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white/50">
              {users.map((user) => (
                <tr 
                  key={user._id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/users/${user._id}`)}
                >
                  <td className="p-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => toggleUserSelection(user._id)}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                          {getInitials(user.name)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center flex-wrap gap-1">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        </div>
                        <div className="text-sm text-gray-500">ID: {user._id.substr(-5)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    <div className="text-sm text-gray-500">{user.phone || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${roleColors[user.role] || 'bg-gray-100 text-gray-800'}`}>
                      {user.role === 'admin' ? 'Administrator' : 
                       user.role === 'partner' ? 'Partener' : 
                       user.role === 'client' ? 'Client' : user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono bg-gray-100 p-2 rounded">
                      {user.idCard?.CNP || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {user.documents && user.documents.id_cardUploaded ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <Check className="h-3 w-3 mr-1" />
                          Buletin
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          <X className="h-3 w-3 mr-1" />
                          Buletin
                        </span>
                      )}
                      
                      {user.documents && user.documents.contractGenerated ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Check className="h-3 w-3 mr-1" />
                          Contract Curs
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          <X className="h-3 w-3 mr-1" />
                          Contract Curs
                        </span>
                      )}
                      
                      {user.documents && user.documents.consultingContractGenerated ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <Check className="h-3 w-3 mr-1" />
                          Contract Consultanță
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          <X className="h-3 w-3 mr-1" />
                          Contract Consultanță
                        </span>
                      )}
                      
                      {user.documents && user.documents.authorityDocumentGenerated ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          <Check className="h-3 w-3 mr-1" />
                          Împuternicire
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          <X className="h-3 w-3 mr-1" />
                          Împuternicire
                        </span>
                      )}
                      
                      {user.signature ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <PenTool className="h-3 w-3 mr-1" />
                          Semnătură
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          <X className="h-3 w-3 mr-1" />
                          Semnătură
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={user.submitted?.status || false}
                          onChange={async () => {
                            try {
                              setIsUpdatingSubmission(user._id);
                              const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
                              
                              const response = await axios.post(
                                `${API_URL}/admin/users/${user._id}/update-submission`,
                                { status: !(user.submitted?.status || false) },
                                {
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${localStorage.getItem('token')}`
                                  }
                                }
                              );

                              // Reîncărcare utilizatori după actualizare
                              if (response.data && response.data.success) {
                                fetchUsers();
                              }
                            } catch (error) {
                              console.error('Error updating submission status:', error);
                              alert(`Eroare la actualizarea stării: ${error.response?.data?.message || error.message}`);
                            } finally {
                              setIsUpdatingSubmission(null);
                            }
                          }}
                          disabled={isUpdatingSubmission === user._id}
                        />
                        <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer ${user.submitted?.status ? 'peer-checked:after:translate-x-full peer-checked:after:border-white peer-checked:bg-blue-600' : ''} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
                      </label>
                      {user.submitted?.updated_by && (
                        <span className="ml-2 text-xs text-gray-500">
                          {user.submitted.updated_by === currentUser._id ? 'de tine' : 
                           (user.submitted.updated_by?.name || 'nespecificat')}
                        </span>
                      )}
                      {isUpdatingSubmission === user._id && (
                        <svg className="animate-spin ml-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={user.ineligible?.status || false}
                          onChange={async () => {
                            try {
                              setIsUpdatingIneligible(user._id);
                              const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
                              
                              const response = await axios.post(
                                `${API_URL}/admin/users/${user._id}/update-ineligible`,
                                { status: !(user.ineligible?.status || false) },
                                {
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${localStorage.getItem('token')}`
                                  }
                                }
                              );

                              // Reîncărcare utilizatori după actualizare
                              if (response.data && response.data.success) {
                                fetchUsers();
                              }
                            } catch (error) {
                              console.error('Error updating ineligible status:', error);
                              alert(`Eroare la actualizarea stării de neeligibilitate: ${error.response?.data?.message || error.message}`);
                            } finally {
                              setIsUpdatingIneligible(null);
                            }
                          }}
                          disabled={isUpdatingIneligible === user._id}
                        />
                        <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer ${user.ineligible?.status ? 'peer-checked:after:translate-x-full peer-checked:after:border-white peer-checked:bg-red-600' : ''} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
                      </label>
                      {user.ineligible?.updated_by && (
                        <span className="ml-2 text-xs text-gray-500">
                          {user.ineligible.updated_by === currentUser._id ? 'de tine' : 
                           (user.ineligible.updated_by?.name || 'nespecificat')}
                        </span>
                      )}
                      {isUpdatingIneligible === user._id && (
                        <svg className="animate-spin ml-2 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.isActive ? (
                      <div className="flex items-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Check className="h-3 w-3 mr-1" />
                          Activ
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <X className="h-3 w-3 mr-1" />
                          Inactiv
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getCreatorName(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center space-x-2">
                      <button 
                        className="p-2 text-orange-600 hover:bg-orange-50 rounded-md transition-colors"
                        onClick={(e) => handleSendEmail(user, e)}
                        title="Trimite email"
                      >
                        <Mail className="h-5 w-5" />
                      </button>
                      
                      {/* Împuternicire Buttons */}
                      {user.documents && user.documents.authorityDocumentGenerated ? (
                        <div className="flex space-x-1">
                          <button 
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                            onClick={(e) => handleDownloadAuthority(user, e)}
                            title={user.signature ? "Descarcă împuternicire (cu semnătură)" : "Descarcă împuternicire (fără semnătură)"}
                          >
                            <FileSignature className="h-5 w-5" />
                          </button>
                          <button 
                            className={`p-2 text-orange-600 hover:bg-orange-50 rounded-md transition-colors ${generatingAuthority === user._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={(e) => handleRegenerateAuthority(user, e)}
                            disabled={generatingAuthority === user._id}
                            title="Regenerează împuternicire"
                          >
                            {generatingAuthority === user._id ? (
                              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <RefreshCw className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      ) : (
                        <button 
                          className={`p-2 text-purple-600 hover:bg-purple-50 rounded-md transition-colors ${generatingAuthority === user._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={(e) => handleGenerateAuthority(user, e)}
                          disabled={generatingAuthority === user._id}
                          title={user.signature ? "Generează împuternicire cu semnătura utilizatorului" : "Generează împuternicire (utilizatorul nu are semnătură)"}
                        >
                          {generatingAuthority === user._id ? (
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <FileSignature className="h-5 w-5" />
                          )}
                        </button>
                      )}
                      
                      <button 
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Sigur doriți să ștergeți utilizatorul ${user.name}?`)) {
                            handleDeleteUser(user._id);
                          }
                        }}
                        title="Șterge utilizator"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                      
                      <button 
                        className="text-blue-600 hover:text-blue-800 px-4 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/users/${user._id}`);
                        }}
                      >
                        <div className="flex items-center">
                          <Eye className="h-5 w-5 mr-2" />
                          <span className="font-medium">Vizualizare</span>
                        </div>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="12" className="px-6 py-10 text-center text-gray-500">
                    Nu există utilizatori care să corespundă criteriilor de căutare.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginare */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(page > 1 ? page - 1 : 1)}
            disabled={page === 1}
            className={`glassmorphism p-2 rounded-lg ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/80'}`}
          >
            <ChevronLeft className="h-5 w-5 text-gray-700" />
          </button>
          
          {Array.from({ length: Math.min(totalPages, 3) }).map((_, i) => {
            // Show current page and adjacent pages
            let pageNumber;
            if (totalPages <= 3) {
              pageNumber = i + 1;
            } else if (page === 1) {
              pageNumber = i + 1;
            } else if (page === totalPages) {
              pageNumber = totalPages - 2 + i;
            } else {
              pageNumber = page - 1 + i;
            }
            
            return (
              <button
                key={pageNumber}
                onClick={() => setPage(pageNumber)}
                className={`h-10 w-10 rounded-lg font-medium ${
                  page === pageNumber
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                    : 'glassmorphism text-gray-700 hover:bg-white/80'
                }`}
              >
                {pageNumber}
              </button>
            );
          })}
          
          <button
            onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}
            disabled={page === totalPages}
            className={`glassmorphism p-2 rounded-lg ${page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/80'}`}
          >
            <ChevronRight className="h-5 w-5 text-gray-700" />
          </button>
        </div>
        
        <div className="text-sm text-gray-500">
          Afișare {users.length > 0 ? (page - 1) * limit + 1 : 0} la {Math.min(page * limit, total)} din {total} rezultate
        </div>
      </div>
    </>
  );
};

export default UsersTable;